import React, { useState } from "react";
import Headline from "../Typography/Headline";
import CategoryItems, { CategoryItem } from "../CategoryItems";
import Button from "../Button";
import { ProductCardProps } from "./type";
import i18next from "i18next";
import { formatPrice } from "~/assets/js/Format";
import SeeIngredientsCTA from "./SeeIngredientsCTA";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import IngredientsOverlay from "./IngredientsOverlay";
import useMeasure from "react-use-measure";
import { useSpring, animated, config } from "@react-spring/web";

const ProductCard = ({ product, inStock, storeId = null }) => {
    const tags = product.attributes.map((attr) => attr.slug);
    const { isDesktop } = useViewport();
    if (product.name.includes("Ramen: Konfigurator")) {
        product["ingredients"] = [
            { name: "Wähle aus" },
            { name: "25+ Zutaten:" },
            { name: "Nudeln" },
            { name: "Veggies" },
            { name: "Proteins" },
            { name: "Toppings" },
        ];
    } else if (product.name.includes("Konfigurator")) {
        product["ingredients"] = [
            { name: "Wähle aus" },
            { name: "60+ Zutaten:" },
            { name: "Salate" },
            { name: "Carbs" },
            { name: "Veggies" },
            { name: "Proteins" },
            { name: "Toppings" },
        ];
    }
    const hasIngredients = product["ingredients"] !== undefined;
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [refCard, { height: cardHeight }] = useMeasure();
    const [refImage, { height: imageHeight }] = useMeasure();
    const localPrice = product.local_prices?.find(
        (local_price) => local_price.store == storeId
    );
    const wasPrice =
        product.was_prices?.find((set) => set.store == storeId) ??
        product.was_prices?.find((set) => set.store == 0);
    const displayPrice = !!localPrice?.price
        ? localPrice?.price
        : product.price;

    const envDetails = {
        energy: product.energy,
        weight: product.weight,
        co2: product.co2,
    };

    const showOverlay = () => setOverlayOpen(true);
    const hideOverlay = () => setOverlayOpen(false);
    const doNothing = () => {};

    const imageExpandAnimation = useSpring({
        maxHeight: overlayOpen ? cardHeight : imageHeight,
        objectFit: overlayOpen ? "cover" : "contain",
        config: { ...config.gentle, duration: 300 },
    });

    return (
        <div className="product-card" ref={refCard}>
            {!isDesktop && (
                <>
                    <IngredientsOverlay
                        productName={product.name}
                        open={overlayOpen}
                        ingredients={product.ingredients}
                        onClose={hideOverlay}
                        envDetails={envDetails}
                        url={product.url}
                    />
                    {overlayOpen && (
                        <animated.img
                            style={imageExpandAnimation}
                            className="product-card__image--overlay"
                            src={product.image.src}
                            srcSet={product.image.srcset}
                            alt={`${product.name} - ${product.image.alt}`}
                        />
                    )}
                </>
            )}
            <div
                style={{ position: "relative" }}
                onMouseEnter={isDesktop ? showOverlay : doNothing}
                onMouseLeave={isDesktop ? hideOverlay : doNothing}>
                <a href={product.url}>
                    <img
                        ref={refImage}
                        className="product-card__image"
                        src={product.image.src}
                        srcSet={product.image.srcset}
                        alt={`${product.name} - ${product.image.alt}`}
                    />
                    {!inStock && (
                        <div className="product-card__sold-out">
                            <p>{i18next.t("productDetails.availableSoon")}</p>
                        </div>
                    )}
                </a>
                {hasIngredients && (
                    <>
                        {!isDesktop && !overlayOpen && (
                            <SeeIngredientsCTA onClick={showOverlay} />
                        )}
                        {isDesktop && (
                            <IngredientsOverlay
                                open={overlayOpen}
                                ingredients={product.ingredients}
                                envDetails={envDetails}
                                url={product.url}
                            />
                        )}
                    </>
                )}
            </div>
            <div className="product-card__body">
                {product.vegan_related_product &&
                    product.vegan_type !== "vegan" && (
                        <a
                            href={product.vegan_related_product}
                            className="product-card__body-annotation">
                            <CategoryItem icon="vegan" />
                        </a>
                    )}
                <div className="product-card__body-content">
                    <Headline
                        className="product-card__body-content-title"
                        size={5}>
                        {product.name}
                    </Headline>
                    <span className="product-card__body-content-price">
                        {wasPrice &&
                            parseFloat(wasPrice.price) > displayPrice && (
                                <s>{formatPrice(wasPrice?.price)}</s>
                            )}{" "}
                        {formatPrice(displayPrice)}
                    </span>
                    <CategoryItems tags={tags} />
                </div>
                <Button
                    size={"SM"}
                    href={product.url}
                    label={i18next.t("complementary.seeProduct")}
                />
            </div>
        </div>
    );
};

ProductCard.propTypes = ProductCardProps;
export default ProductCard;
